//Login form

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import Input from '../common/Input';
import Button from '../common/Button';
import { authService } from '../../services/auth';
import { setUser, setError } from '../../store/slices/authSlice';
import { LoginForm as LoginFormType } from '../../types';

interface LoginFormProps {
  onBack: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onBack }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<LoginFormType>({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setErrorState] = useState<string>('');

  const handleInputChange = (field: keyof LoginFormType, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setErrorState('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorState('');

    try {
      const user = await authService.signIn(formData);
      dispatch(setUser(user));
    } catch (error: any) {
      const errorMessage = error.message || 'There was an error, perhaps your username and/or password is incorrect';
      setErrorState(errorMessage);
      dispatch(setError(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-powder-200">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-powder-800 mb-2">
          Welcome Back!
        </h2>
        <p className="text-powder-600">
          Log in to continue your forest journey
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
        >
          <p className="text-red-700 text-sm">{error}</p>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Username"
          type="text"
          value={formData.username}
          onChange={(value) => handleInputChange('username', value)}
          placeholder="Enter your username"
          required
          disabled={isLoading}
        />

        <Input
          label="Password"
          type="password"
          value={formData.password}
          onChange={(value) => handleInputChange('password', value)}
          placeholder="Enter your password"
          required
          disabled={isLoading}
        />

        <div className="space-y-4">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isLoading || !formData.username || !formData.password}
          >
            {isLoading ? 'Logging in...' : 'Log In'}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="md"
            fullWidth
            onClick={onBack}
            disabled={isLoading}
          >
            Back
          </Button>
        </div>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          className="text-sm text-sage-600 hover:text-sage-800 transition-colors"
          disabled={isLoading}
        >
          Forgot password?
        </button>
      </div>
    </div>
  );
};

export default LoginForm;
